import { API_URL } from "../../configs/_api";
import { useNoti } from "../../providers";
import ApiRequest from "../../utils/apiRequest";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import {
    committeeColumns,
    memberColumns,
    shopColumns,
    productColumns,
    invoiceListColumns,
    loanContractColumns,
    depositContractColumns,
    settlementColumns,
    fundingTransferColumns,
    fundReceivingInvoiceColumns,
    fundAllocationColumns,
    revenueTransfersColumns,
    articleListColumns,
    invoiceSummaryColumns,
    ecommerceOrderColumns,
    ecommerceOrderSummaryColumns,
    ecommerceOrderForSqlColumns,
    articleLogListColumns,
    productLogListColumns
} from "../../configs/_dataColumnConfig"
import { getTotalAmountFromArray } from "../../utils/arrayFunc";
import { useMetaData } from "../meta_data";
import { formatVal } from "../../utils/string";
import { PAYMENT_METHOD } from "../../enums/payment-method.enum";

const useDataExportActions = () => {
    const { showNoti } = useNoti();

    const { donors, fetchDonors } = useMetaData();

const insertDonorColumn = async (columns) => {
    await fetchDonors();
    let donor_columns = [];
    donors.forEach(donor => {
        donor_columns.push({id: donor.id, label: donor.label})
    })
    return [
        ...columns,
        ...donor_columns
    ]
};

    const excelFormat = Object.freeze({
        CSV: 'csv',
        XLSX: 'xlsx'
    });

    const moduleParam = {
        COMMITTEE: 'committee',
        MEMBER: 'member',
        SHOP: 'shop',
        DEPOSIT_CONTRACT: 'deposit_contract',
        LOAN_CONTRACT: 'loan_contract',
        SETTLEMENT: 'settlement',
        FUNDING_TRANSFER: 'funding_transfer',
        FUND_RECEIVING_INVOICE: 'fund_receiving_invoice',
        FUND_ALLOCATION: 'fund_allocation',
        REVENUE_TRANSFERS: 'revenue_transfers',
        PRODUCT: 'product',
        INVOICE_LIST : 'invoice',
        ARTICLE_LIST : 'article',
        ARTICLE_LOG_LIST : 'article_log',
        PRODUCT_LOG_LIST : 'product_log',
        INVOICESUMMARY : 'invoiceSummary',
        ECOMMERECE_ORDER: 'ecommerce_order',
        ECOMMERECE_ORDER_SUMMARY: 'ecommerce_orderSummary',
        ECOMMERECE_ORDER_FOR_SQL: 'ecommerce_order_for_sql'
    }

    async function getData(moduleParamName, filter={}) {
        try {
            const { dateFrom, dateTo, committee_id, member_type, product_type, article_id, product_id, search_term, region_id, township_id, village_tract_id, village_id, with_completion} = filter;
            let queryParams = committee_id 
            ? 
            `module=${moduleParamName}&filter[start_date]=${dateFrom}&filter[end_date]=${dateTo}&filter[committee_id]=${committee_id}&filter[member_type]=${member_type}` 
            : 
            `module=${moduleParamName}&filter[start_date]=${dateFrom}&filter[end_date]=${dateTo}&filter[member_type]=${member_type}&filter[product_type]=${product_type}`;

            if(moduleParamName == moduleParam.ARTICLE_LOG_LIST){
                queryParams = `module=${moduleParamName}&filter[start_date]=${dateFrom}&filter[end_date]=${dateTo}&filter[member_type]=${member_type}&filter[article_id]=${article_id}&filter[search_term]=${search_term}`;
            }

            if(moduleParamName == moduleParam.PRODUCT_LOG_LIST){
                queryParams = `module=${moduleParamName}&filter[start_date]=${dateFrom}&filter[end_date]=${dateTo}&filter[member_type]=${member_type}&filter[product_id]=${product_id}&filter[search_term]=${search_term}`;
            }

            if(moduleParamName == moduleParam.ECOMMERECE_ORDER){
                queryParams = `module=${moduleParamName}&filter[start_date]=${dateFrom}&filter[end_date]=${dateTo}&filter[region]=${region_id}&filter[township]=${township_id}&filter[village_tract]=${village_tract_id}&filter[village]=${village_id}`;
            }

            if(moduleParamName == moduleParam.ECOMMERECE_ORDER_FOR_SQL){
                queryParams = `module=${moduleParamName}&filter[start_date]=${dateFrom}&filter[end_date]=${dateTo}&filter[region]=${region_id}&filter[township]=${township_id}&filter[village_tract]=${village_tract_id}&filter[village]=${village_id}`;
            }

            if( with_completion ){
                queryParams += `&filter[with_completion]=${with_completion}`;
            }

            const url = `${API_URL.DataExport}?${queryParams}`;
            const res = await ApiRequest.post(url, null);
            const data = res.data.payload.data.map((order) => ({
                ...order,
                payment_method: typeof order.payment_method === 'string' 
                    ? order.payment_method 
                    : (PAYMENT_METHOD[order.payment_method] || 'Unknown')
            }));

            console.log('data ', data)

            return data;
        }
        catch(err) {
            const errMsg = err.response?.data.errors.message || err.message;
            showNoti(errMsg, "error");
            return Promise.reject();
        }
    }

    async function getColumnConfig(moduleParamName) {
        switch(moduleParamName) {
            case moduleParam.COMMITTEE: return committeeColumns;
            case moduleParam.MEMBER: return await insertDonorColumn(memberColumns);
            case moduleParam.SHOP: return shopColumns;
            case moduleParam.DEPOSIT_CONTRACT: return depositContractColumns;
            case moduleParam.LOAN_CONTRACT: return loanContractColumns;
            case moduleParam.SETTLEMENT: return settlementColumns;
            case moduleParam.FUNDING_TRANSFER: return fundingTransferColumns;
            case moduleParam.FUND_RECEIVING_INVOICE: return fundReceivingInvoiceColumns;
            case moduleParam.FUND_ALLOCATION: return fundAllocationColumns;
            case moduleParam.REVENUE_TRANSFERS: return revenueTransfersColumns;
            case moduleParam.PRODUCT: return productColumns;
            case moduleParam.INVOICE_LIST: return invoiceListColumns;
            case moduleParam.INVOICESUMMARY: return invoiceSummaryColumns
            case moduleParam.ARTICLE_LIST: return articleListColumns;
            case moduleParam.ECOMMERECE_ORDER: return ecommerceOrderColumns;
            case moduleParam.ECOMMERECE_ORDER_SUMMARY: return ecommerceOrderSummaryColumns;
            case moduleParam.ECOMMERECE_ORDER_FOR_SQL: return ecommerceOrderForSqlColumns;
            case moduleParam.ARTICLE_LOG_LIST: return articleLogListColumns;
            case moduleParam.PRODUCT_LOG_LIST: return productLogListColumns;
            default: return null;
        }
    } 

    function getFilteredData(data=[], columnConfig=[], module = '') {
        return data.map(row => {
            const filteredRow = columnConfig.reduce((obj, column) => {
                if (module === moduleParam.MEMBER) {
                    if(
                        row?.funded_amount &&
                        Array.isArray(row?.funded_amount) &&
                        (row.funded_amount.filter(fund => fund.donor_id === column.id)).length > 0
                    ){
                        obj[column.id] = row.funded_amount.filter(fund => fund.donor_id === column.id)[0].amount;
                    } else {
                        obj[column.id] = row[column.id];
                    }
                    return obj;
                } else {
                    obj[column.id] = row[column.id];
                    if(Array.isArray(row[column.id])){
                        obj[column.id] = getTotalAmountFromArray(row[column.id]);
                    }
                    // for ecommerce order export - product_list_data is object
                    if(row && row?.product_list_data) {
                        Object.entries(row.product_list_data).map(([key, value]) => {
                            if(column.id === key) {
                                obj[key] = value;
                            }
                        })
                    }
                    return obj;
                }
            }, {});
            return filteredRow;
        })
    }

    // function mergeData(data) {
    //     const resultMap = {};

    //     data.forEach(item => {
    //       const memberId = item.member_id;
          
    //       if (!resultMap[memberId]) {
    //         resultMap[memberId] = { ...item };
    //       } else {
    //         resultMap[memberId].cash_down_amount += item.cash_down_amount;
    //         resultMap[memberId].point_pay_amount += item.point_pay_amount;
    //         resultMap[memberId].total_amount += item.total_amount;
    //       }
    //     });
      
    //     return Object.values(resultMap).map(item => {
    //       return {
    //         ...item,
    //         cash_down_amount: resultMap[item.member_id] === item ? item.cash_down_amount : 0,
    //         point_pay_amount: resultMap[item.member_id] === item ? item.point_pay_amount : 0,
    //       };
    //     });
    //   }

    async function downloadData(moduleParamName, filter, fileFormat, fileName) {
        try {
            const data = (await getData(moduleParamName, filter)) || [];
            const columnConfig = await getColumnConfig(moduleParamName);

            if( moduleParamName === 'ecommerce_order' ){
                // Header //
                // 1. Find the highest length
                const maxLength = Math.max(...data.map(item => item.delivery_history?.length || 0));

                // 2. Find the actual longest array (the one that has that maxLength)
                const longestDeliveryRecord = data.find(item => (item.delivery_history?.length || 0) === maxLength);

                if (longestDeliveryRecord && longestDeliveryRecord.delivery_history) {
                    longestDeliveryRecord.delivery_history.map((delivery, index) => {
                        columnConfig.push({ 
                            id: `delivery_history_deliveried_quantity_${index + 1}`,
                            label: `Deliveried Quantity ${index + 1}`
                        });
                        columnConfig.push({ 
                            id: `delivery_history_date_${index + 1}`,
                            label: `Deliveried Date ${index + 1}`
                        });
                    });
                }
                // Header //

                // Data //
                data.forEach(item => {
                    let remainingItemCount = item.product_list_data.qty;
                    if( Array.isArray(item.delivery_history) && item.delivery_history.length > 0 ) {
                        item.delivery_history.forEach((deliveryHistory, index) => {
                            remainingItemCount -= deliveryHistory.deliveredQty;
                            item[`delivery_history_deliveried_quantity_${index + 1}`] = deliveryHistory.deliveredQty;
                            item[`delivery_history_date_${index + 1}`] = formatVal(deliveryHistory.deliveredDate, "date-only").substring(0, 11);
                        });
                    }
                    item['remaining_item_count'] = remainingItemCount;
                })
                // Data //
            }

            const filteredData = getFilteredData(data, columnConfig, moduleParamName);
            const sheetHeader = [columnConfig.map(column => column.label)];

            const workBook = XLSX.utils.book_new();
            const workSheet = XLSX.utils.json_to_sheet([]);
            XLSX.utils.sheet_add_aoa(workSheet, sheetHeader);
            XLSX.utils.sheet_add_json(workSheet, filteredData, { origin: 'A2', skipHeader: true });
            XLSX.utils.book_append_sheet(workBook, workSheet, 'sheet 1');

            if(moduleParamName === 'invoice'){
                 
                const summaryData =  Object.values(data.reduce((acc, curr) => {
                    if (!acc[curr.invoice_id]) {
                        acc[curr.invoice_id] = curr;
                    }
                    return acc;
                }, {}));
               
                const columnConfigForSummary = await getColumnConfig('invoiceSummary');

                const filteredDataForSummary = getFilteredData(summaryData, columnConfigForSummary);
                const summarySheetHeader = [columnConfigForSummary.map(column => column.label)];
                const summaryWorkSheet = XLSX.utils.json_to_sheet([]);
                XLSX.utils.sheet_add_aoa(summaryWorkSheet, summarySheetHeader);
                XLSX.utils.sheet_add_json(summaryWorkSheet, filteredDataForSummary, { origin: 'A2', skipHeader: true });
                XLSX.utils.book_append_sheet(workBook, summaryWorkSheet, 'Invoice Summary');
            }

            if(moduleParamName === 'ecommerce_order') {
                const summaryData =  Object.values(data.reduce((acc, curr) => {
                    if (!acc[curr.order_id]) {
                        acc[curr.order_id] = { 
                            ...curr,
                            remaining_total_item: Number(curr.remaining_item_count) || 0,
                        };
                    } else {
                        acc[curr.order_id].delivery_history = [
                            ...(acc[curr.order_id].delivery_history || []),
                            ...(curr.delivery_history || [])
                        ];

                        acc[curr.order_id].remaining_total_item += Number(curr.remaining_item_count) || 0;
                    }
                    return acc;
                }, {}));

                const columnConfigForSummary = await getColumnConfig('ecommerce_orderSummary');

                // Delivery History //
                const formattedSummary = summaryData.map(order => {
                    // We are reducing the history array into a single "Map" of unique dates
                    const historyByDate = order.delivery_history.reduce((dateMap, currentRow) => {
                        const dateKey = currentRow.deliveredDate;
                        const quantity = Number(currentRow.deliveredQty) || 0;

                        if (!dateMap[dateKey]) {
                            dateMap[dateKey] = {
                                date: dateKey,
                                deliQty: quantity
                            };
                        } else {
                            dateMap[dateKey].deliQty += quantity;
                        }

                        return dateMap;
                    }, {});

                    return {
                        ...order,
                        delivered_history: Object.values(historyByDate)
                    };
                });
                
                const maxLengthDeli = Math.max(...formattedSummary.map(item => item.delivered_history?.length || 0));
                const longestDeliRecord = formattedSummary.find(item => (item.delivered_history?.length || 0) === maxLengthDeli);

                if( longestDeliRecord && longestDeliRecord.delivered_history ){
                    longestDeliRecord.delivered_history.map((deli, index) => {
                        columnConfigForSummary.push({
                            id: `delivered_qty_${index + 1}`,
                            label: `Total Delivered Item ${index + 1}`
                        });

                        columnConfigForSummary.push({
                            id: `delivered_date_${index + 1}`,
                            label: `Delivered Date ${index + 1}`
                        });
                    })
                }

                formattedSummary.forEach(summary => {
                    if( Array.isArray(summary.delivered_history) && summary.delivered_history.length > 0 ){
                        summary.delivered_history.forEach((deli, index) => {
                            summary[`delivered_qty_${index + 1}`] = deli.deliQty;
                            summary[`delivered_date_${index + 1}`] = formatVal(deli.date, "date-only").substring(0, 11);
                        })
                    }
                })
                // Delivery History //

                // Recevied Payment //
                // Summary Header //
                // 1. Find the highest length
                const maxLengthSummary = Math.max(...data.map(item => item.receive_payments?.length || 0));

                // 2. Find the actual longest array (the one that has that maxLength)
                const longestPaymentRecord = data.find(item => (item.receive_payments?.length || 0) === maxLengthSummary);

                if (longestPaymentRecord && longestPaymentRecord.receive_payments) {
                    longestPaymentRecord.receive_payments.map((payment, index) => {
                        columnConfigForSummary.push({ 
                            id: `receive_payment_amount_${index + 1}`,
                            label: `Receive Payments Amount ${index + 1}`
                        });
                        columnConfigForSummary.push({ 
                            id: `receive_payment_date_${index + 1}`,
                            label: `Receive Payments Date ${index + 1}`
                        });
                    });
                }
                // Summary Header //

                // Summary Data //
                formattedSummary.forEach(summaryItem => {
                    if( Array.isArray(summaryItem.receive_payments) && summaryItem.receive_payments.length > 0 ){
                        summaryItem.receive_payments.forEach((payment, index) => {
                            summaryItem[`receive_payment_amount_${index + 1}`] = payment.amount;
                            summaryItem[`receive_payment_date_${index + 1}`] = formatVal(payment.payment_date, "date-only").substring(0, 11);
                        })

                        summaryItem['remaining_payment'] = summaryItem.remaining_balance;
                    }
                });
                // Summary Data //
                // Recevied Payment //

                const summarySheetHeader = [columnConfigForSummary.map(column => column.label)];
                const filteredDataForSummary = getFilteredData(formattedSummary, columnConfigForSummary);

                const summaryWorkSheet = XLSX.utils.json_to_sheet([]);
                XLSX.utils.sheet_add_aoa(summaryWorkSheet, summarySheetHeader);
                XLSX.utils.sheet_add_json(summaryWorkSheet, filteredDataForSummary, { origin: 'A2', skipHeader: true });
                XLSX.utils.book_append_sheet(workBook, summaryWorkSheet, 'Ecommerce Order Summary');
            }

            if(fileFormat == excelFormat.XLSX) {
                return XLSX.writeFile(workBook, fileName);
            }

            const buffer = XLSX.write(workBook, {bookType: "csv", type:'array'});
            const blob = new Blob([buffer], { type: "text/csv" });
            saveAs(blob, fileName);
        }
        catch(err) {
            console.error(err.stack);
            return Promise.reject();
        }
    }

    return {
        excelFormat,
        moduleParam,

        getFilteredData,
        downloadData
    }
}

export default useDataExportActions;